import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { carrierSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as countriesActionCreators from '../countries/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { Country } from '../countries/model'
import { Carrier } from './model'
import * as actionCreators from './actionCreators'
import * as actionTypes from './actionTypes'

// Fetch

const fetchCarriersEpic = (action$, store) => {
    const tableIdentifier = 'carriers_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: 'carriers/list',
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(carrierSchema)
                        )

                        const carriers = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier, normalizedData.result)
                        const countries = normalizedEntitiesToRecordMap(normalizedData.entities.countries, Country)

                        const observables = [
                            Observable.of(actionCreators.fetchCarriersFulfilled(carriers)),
                            Observable.of(countriesActionCreators.fetchCountriesFulfilled(countries)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCarriersRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCarrierEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().carriers.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `carriers/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, carrierSchema)

                        const carriers = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier)
                        const countries = normalizedEntitiesToRecordMap(normalizedData.entities.countries, Country)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCarriersFulfilled(carriers)),
                            Observable.of(actionCreators.fetchCarrierFulfilled()),
                            Observable.of(countriesActionCreators.fetchCountriesFulfilled(countries))
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCarriersRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCarrierEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().carriers.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = { ...action.payload.values }
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = 'carriers'
            let method = 'POST'

            if (values.id) {
                path = `carriers/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveCarrierDuplicityFound(ajaxResponse.response.duplicity)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, carrierSchema)

                        const carriers = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier)
                        const carrier = carriers.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCarrierFulfilled(carrier)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCarrierRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCarrierEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().carriers.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `carriers/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCarrierFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCarrierRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )


// Restore

const restoreCarrierEpic = (action$, store) =>
    action$
        .ofType(actionTypes.RESTORE)
        .filter(() => !store.getState().carriers.getIn(['restoring', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.RESTORE_STARTED,
                }),
                request({
                    path: `carriers/${action.payload}/restore`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, carrierSchema)
                        const items = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier)
                        const item = items.valueSeq().first()
                        return Observable.concat(Observable.of(actionCreators.restoreCarrierFulfilled(item)))
                    })
                    .catch(error => Observable.of(actionCreators.restoreCarrierRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.RESTORE_CANCELLED))
            )
        )

// Load info by VAT

const loadInfoByVATEpic = (action$, store) =>
    action$
        .ofType(actionTypes.LOAD_INFO)
        .filter(() => !store.getState().customers.getIn(['infoByVAT', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.LOAD_INFO_STARTED,
                }),
                request({
                    path: `carriers/find-info?vat_number=${action.payload}`,
                    method: 'POST',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.loadInfoByVATNumberFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.loadInfoByVATNumberRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.LOAD_INFO_CANCELLED))
            )
        )

// Export

const exportCarriersEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `carriers/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

// Contacts

const fetchCarrierContactsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_CONTACTS)
        .filter(() => !store.getState().carriers.getIn(['contacts', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_CONTACTS_STARTED,
                }),
                request({
                    path: `carriers/${action.payload.id}/contacts${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchCarrierContactsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchCarrierContactsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CONTACTS_CANCELLED))
            )
        )

const exportCarrierContactsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_CONTACTS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `carriers/${action.payload}/contacts/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_CONTACTS_FULFILLED,
            })
        )
    })

// Check duplicity

const checkCarrierDuplicityEpic = (action$, store) =>
    action$
        .ofType(actionTypes.CHECK_DUPLICITY)
        .filter(() => !store.getState().carriers.getIn(['checkingDuplicity', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.CHECK_DUPLICITY_STARTED,
                }),
                request({
                    path: `carriers/check-duplicity`,
                    method: 'POST',
                    body: action.payload,
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.checkCarrierDuplicityFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.checkCarrierDuplicityRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.CHECK_DUPLICITY_CANCELLED))
            )
        )

export default [
    fetchCarriersEpic,
    fetchCarrierEpic,
    saveCarrierEpic,
    deleteCarrierEpic,
    restoreCarrierEpic,
    loadInfoByVATEpic,
    exportCarriersEpic,
    fetchCarrierContactsEpic,
    exportCarrierContactsEpic,
    checkCarrierDuplicityEpic,
]
